// src/components/common/ApiKeysPage.js
import { useState, useEffect, useCallback } from 'react';
import {
  Key, Lock, Copy, Eye, EyeOff, Trash2, Plus, RefreshCw,
  AlertCircle, CheckCircle2, Code2, Terminal, Globe, Shield,
  BookOpen, Building2, Zap,
} from 'lucide-react';
import { fetchMyApiKeys, generateMyApiKey, revokeApiKey } from '../../services/adminService';
import { fetchMySoilDevices } from '../../services/soilService';
import { USER_ROLES } from '../../utils/roles';
import { API_BASE_URL } from '../../services/config';

const DATA_BASE = `${API_BASE_URL}/data`;
const SOIL_BASE = `${API_BASE_URL}/soil`;

// Which users can use the API
const userCanAccessApi = (userData, userRole) => {
  if (userRole === USER_ROLES.CUSTODIAN) return true;
  const tier = userData?.accessTier;
  return ['research', 'enterprise', 'custom'].includes(tier);
};

const timeAgo = (iso) => {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1)  return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${d}d ago`;
};

// ── Code block with copy button ───────────────────────────────────────────────
const CodeBlock = ({ code, language = 'bash' }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ position: 'relative', background: '#0f172a', borderRadius: 12, overflow: 'hidden', border: '1px solid #1e293b' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', background: '#1e293b', borderBottom: '1px solid #334155' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{language}</span>
        <button onClick={copy} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: copied ? '#10b981' : '#94a3b8', fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 5 }}>
          {copied ? <CheckCircle2 style={{ width: 12, height: 12 }} /> : <Copy style={{ width: 12, height: 12 }} />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre style={{ margin: 0, padding: '14px 16px', fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace", fontSize: 12, lineHeight: 1.7, color: '#e2e8f0', overflowX: 'auto', whiteSpace: 'pre' }}>
        {code}
      </pre>
    </div>
  );
};

// ── Locked / upgrade prompt ───────────────────────────────────────────────────
const LockedState = ({ userData, onNavigate }) => (
  <div style={{ maxWidth: 520, margin: '60px auto', textAlign: 'center', padding: '0 20px' }}>
    <div style={{ width: 72, height: 72, borderRadius: 20, background: '#f1f5f9', border: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
      <Lock style={{ width: 32, height: 32, color: '#94a3b8' }} />
    </div>
    <h2 style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', margin: '0 0 10px' }}>API Access Not Available</h2>
    <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, margin: '0 0 6px' }}>
      Your current plan (<strong style={{ color: '#334155' }}>{(userData?.accessTier || 'basic').charAt(0).toUpperCase() + (userData?.accessTier || 'basic').slice(1)}</strong>) does not include programmatic API access.
    </p>
    <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, margin: '0 0 28px' }}>
      Upgrade to access real-time weather data via API key for custom integrations, research pipelines, and automated monitoring.
    </p>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 28 }}>
      {[
        { label: 'Research',   price: 'KES 2,500/mo', color: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd', icon: BookOpen },
        { label: 'Enterprise', price: 'Custom',        color: '#059669', bg: '#ecfdf5', border: '#6ee7b7', icon: Building2 },
        { label: 'Custom',     price: 'KES 1,000+',   color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: Zap },
      ].map(({ label, price, color, bg, border, icon: Icon }) => (
        <div key={label} style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
          <Icon style={{ width: 18, height: 18, color, margin: '0 auto 6px', display: 'block' }} />
          <p style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', margin: 0 }}>{label}</p>
          <p style={{ fontSize: 11, color: '#64748b', margin: '3px 0 0' }}>{price}</p>
        </div>
      ))}
    </div>

    <button
      onClick={() => onNavigate && onNavigate('subscription')}
      style={{ padding: '12px 32px', background: 'linear-gradient(135deg, #065f46, #047857)', color: '#fff', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
    >
      View Plans & Upgrade
    </button>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ApiKeysPage({ userData, userRole, onNavigate }) {
  const hasAccess = userCanAccessApi(userData, userRole);
  const isCustodian = userRole === USER_ROLES.CUSTODIAN;
  const assignedDevices = userData?.assignedDevices || [];
  const tier = userData?.accessTier || 'basic';

  const [keys,         setKeys]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [generating,   setGenerating]   = useState(false);
  const [newKeySecret, setNewKeySecret] = useState(null);
  const [keyName,      setKeyName]      = useState('');
  const [showName,     setShowName]     = useState(false);
  const [keyVisible,   setKeyVisible]   = useState(false);
  const [error,        setError]        = useState('');
  const [revoking,     setRevoking]     = useState(null);
  const [soilDevices,  setSoilDevices]  = useState([]);
  const [docsTab,      setDocsTab]      = useState('weather'); // 'weather' | 'soil'

  const loadKeys = useCallback(async () => {
    if (!hasAccess) return;
    setLoading(true);
    try {
      const res = await fetchMyApiKeys();
      setKeys((res.keys || []).filter(k => k.isActive));
    } catch (e) {
      setError(e.message || 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  }, [hasAccess]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  useEffect(() => {
    if (!hasAccess) return;
    fetchMySoilDevices().then(setSoilDevices).catch(() => {});
  }, [hasAccess]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const name = keyName.trim() || 'My API Key';
      const res = await generateMyApiKey(name);
      setNewKeySecret(res.key);
      setKeyName('');
      setShowName(false);
      setKeyVisible(false);
      await loadKeys();
    } catch (e) {
      setError(e.message || 'Failed to generate key');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this API key? Any integrations using it will stop working immediately.')) return;
    setRevoking(id);
    try {
      await revokeApiKey(id);
      setKeys(prev => prev.filter(k => k.id !== id));
    } catch (e) {
      setError(e.message || 'Failed to revoke key');
    } finally {
      setRevoking(null);
    }
  };

  const exampleDevice     = assignedDevices[0] || 'DEVICE_ID';
  const exampleSoilDevice = soilDevices[0]?.deviceId || 'SOIL_DEVICE_ID';

  if (!hasAccess) return <LockedState userData={userData} onNavigate={onNavigate} />;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px 20px 60px' }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Key style={{ width: 20, height: 20, color: '#fff' }} />
            </div>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', margin: 0 }}>Developer API</h1>
              <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
                {isCustodian
                  ? `Access your ${assignedDevices.length} assigned station${assignedDevices.length !== 1 ? 's' : ''} programmatically`
                  : 'Programmatic access to all weather station data'}
              </p>
            </div>
          </div>
        </div>

        {/* Access badge */}
        <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Shield style={{ width: 18, height: 18, color: '#16a34a' }} />
          </div>
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: 0 }}>
              API Access Active
              <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 700, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', padding: '2px 8px', borderRadius: 20 }}>
                {isCustodian ? 'Custodian · Free' : tier.charAt(0).toUpperCase() + tier.slice(1)}
              </span>
            </p>
            <p style={{ fontSize: 12, color: '#64748b', margin: '3px 0 0' }}>
              {isCustodian
                ? `Scoped to your ${assignedDevices.length} assigned device(s)`
                : 'Full access to all 28+ weather stations'}
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* Left column: Keys */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Generate key */}
            <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 16, padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <p style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', margin: 0 }}>API Keys</p>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{keys.length}/3 active</span>
              </div>

              {/* New key secret banner */}
              {newKeySecret && (
                <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 12, padding: '14px', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <AlertCircle style={{ width: 14, height: 14, color: '#d97706', flexShrink: 0 }} />
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#92400e', margin: 0 }}>Save this key — it won't be shown again</p>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <code style={{ flex: 1, background: '#0f172a', color: '#34d399', fontSize: 11, padding: '8px 10px', borderRadius: 8, fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.5 }}>
                      {keyVisible ? newKeySecret : newKeySecret.slice(0, 14) + '•'.repeat(28)}
                    </code>
                    <button onClick={() => setKeyVisible(v => !v)} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 7, padding: 7, cursor: 'pointer', color: '#64748b' }}>
                      {keyVisible ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
                    </button>
                    <button onClick={() => { navigator.clipboard.writeText(newKeySecret).catch(() => {}); }} style={{ background: '#065f46', border: 'none', borderRadius: 7, padding: 7, cursor: 'pointer', color: '#fff' }}>
                      <Copy style={{ width: 14, height: 14 }} />
                    </button>
                  </div>
                  <button onClick={() => setNewKeySecret(null)} style={{ marginTop: 8, fontSize: 11, color: '#92400e', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                    Done, I've saved it
                  </button>
                </div>
              )}

              {/* Generate form */}
              {keys.length < 3 && (
                <div style={{ marginBottom: 14 }}>
                  {showName ? (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input
                        value={keyName}
                        onChange={e => setKeyName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                        placeholder="Key name (e.g. Production App)"
                        style={{ flex: 1, padding: '8px 12px', fontSize: 13, border: '1.5px solid #e2e8f0', borderRadius: 9, outline: 'none', fontFamily: 'inherit' }}
                      />
                    </div>
                  ) : null}
                  <button
                    onClick={() => showName ? handleGenerate() : setShowName(true)}
                    disabled={generating}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px', background: generating ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: generating ? 'not-allowed' : 'pointer' }}
                  >
                    {generating
                      ? <><RefreshCw style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> Generating…</>
                      : <><Plus style={{ width: 14, height: 14 }} /> {showName ? 'Create Key' : 'Generate New Key'}</>}
                  </button>
                  {showName && <button onClick={() => { setShowName(false); setKeyName(''); }} style={{ width: '100%', marginTop: 6, padding: '7px', background: 'none', border: '1px solid #e2e8f0', borderRadius: 9, fontSize: 12, color: '#64748b', cursor: 'pointer' }}>Cancel</button>}
                </div>
              )}

              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 9, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#991b1b' }}>
                  {error}
                </div>
              )}

              {/* Keys list */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <RefreshCw style={{ width: 18, height: 18, color: '#94a3b8', animation: 'spin 1s linear infinite', display: 'block', margin: '0 auto' }} />
                </div>
              ) : keys.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13 }}>
                  No active API keys. Generate one above.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {keys.map(k => (
                    <div key={k.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 11, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: 0 }}>{k.name}</p>
                          <code style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>{k.keyPrefix}••••••••••••</code>
                          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                            <span style={{ fontSize: 10, color: '#94a3b8' }}>Last used: {timeAgo(k.lastUsed)}</span>
                            <span style={{ fontSize: 10, color: '#94a3b8' }}>{k.requestCount || 0} requests</span>
                          </div>
                          {k.deviceIds && !k.deviceIds.includes('*') && (
                            <p style={{ fontSize: 10, color: '#94a3b8', margin: '3px 0 0' }}>
                              {k.deviceIds.length} device{k.deviceIds.length !== 1 ? 's' : ''}
                            </p>
                          )}
                          {k.deviceIds?.includes('*') && (
                            <p style={{ fontSize: 10, color: '#16a34a', margin: '3px 0 0', fontWeight: 600 }}>All devices</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleRevoke(k.id)}
                          disabled={revoking === k.id}
                          style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: '#fff', border: '1px solid #fecaca', borderRadius: 8, color: '#ef4444', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                        >
                          {revoking === k.id ? <RefreshCw style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} /> : <Trash2 style={{ width: 11, height: 11 }} />}
                          Revoke
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Assigned devices (custodians + all-access) */}
            {(assignedDevices.length > 0 || soilDevices.length > 0) && (
              <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 16, padding: '20px' }}>
                <p style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', margin: '0 0 12px' }}>Your API Device Scope</p>

                {/* Weather stations */}
                {assignedDevices.length > 0 && (
                  <>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>
                      Weather Stations ({assignedDevices.length})
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: soilDevices.length > 0 ? 14 : 0 }}>
                      {assignedDevices.map(id => (
                        <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 9 }}>
                          <div style={{ width: 7, height: 7, borderRadius: 4, background: '#16a34a', flexShrink: 0 }} />
                          <code style={{ fontSize: 11, color: '#1e293b', fontFamily: 'monospace', flex: 1 }}>{id}</code>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Soil sensors */}
                {soilDevices.length > 0 && (
                  <>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>
                      Soil Sensors ({soilDevices.length})
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {soilDevices.map(d => (
                        <div key={d.deviceId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 9 }}>
                          <div style={{ width: 7, height: 7, borderRadius: 4, background: '#ca8a04', flexShrink: 0 }} />
                          <code style={{ fontSize: 11, color: '#1e293b', fontFamily: 'monospace', flex: 1 }}>{d.deviceId}</code>
                          {d.name && <span style={{ fontSize: 10, color: '#92400e' }}>{d.name}</span>}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right column: Docs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Endpoints — tabbed: Weather / Soil */}
            <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 16, padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Globe style={{ width: 15, height: 15, color: '#6366f1' }} />
                  <p style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', margin: 0 }}>Endpoints</p>
                </div>
                {/* Tab switcher */}
                <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
                  {[['weather', '🌤 Weather'], ['soil', '🌱 Soil']].map(([tab, label]) => (
                    <button key={tab} onClick={() => setDocsTab(tab)} style={{
                      padding: '4px 10px', fontSize: 11, fontWeight: 700, border: 'none', borderRadius: 6, cursor: 'pointer',
                      background: docsTab === tab ? '#fff' : 'transparent',
                      color: docsTab === tab ? '#1e293b' : '#64748b',
                      boxShadow: docsTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    }}>{label}</button>
                  ))}
                </div>
              </div>

              {docsTab === 'weather' ? (
                <CodeBlock language="http" code={
`# Historical readings for a station
GET ${DATA_BASE}/readings/${exampleDevice}
    ?from=2025-01-01&to=2025-01-31

# Latest reading for a station
GET ${DATA_BASE}/readings/${exampleDevice}/latest

# Latest from all your stations
GET ${DATA_BASE}/readings/latest`} />
              ) : (
                <CodeBlock language="http" code={
`# All your assigned soil sensors
GET ${SOIL_BASE}/devices

# Latest reading from all soil sensors
GET ${SOIL_BASE}/readings/latest

# Historical readings for a soil sensor
GET ${SOIL_BASE}/readings/device/${exampleSoilDevice}
    ?startDate=2025-01-01&limit=200

# Soil readings at a weather station
GET ${SOIL_BASE}/readings/station/${exampleDevice}`} />
              )}

              <div style={{ marginTop: 10, padding: '10px 12px', background: '#fefce8', border: '1px solid #fef08a', borderRadius: 9 }}>
                <p style={{ fontSize: 11, color: '#713f12', margin: 0, lineHeight: 1.6 }}>
                  <strong>Auth header:</strong>{' '}
                  <code style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 3 }}>Authorization: ApiKey YOUR_KEY</code>
                  <br />Or Bearer token:{' '}
                  <code style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 3 }}>Authorization: Bearer YOUR_JWT</code>
                </p>
              </div>
            </div>

            {/* cURL example */}
            <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 16, padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Terminal style={{ width: 15, height: 15, color: '#6366f1' }} />
                <p style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', margin: 0 }}>cURL Example</p>
              </div>
              {docsTab === 'weather' ? (
                <CodeBlock language="bash" code={
`# Latest weather reading
curl -H "Authorization: ApiKey YOUR_KEY" \\
  "${DATA_BASE}/readings/${exampleDevice}/latest"

# Last 7 days
curl -H "Authorization: ApiKey YOUR_KEY" \\
  "${DATA_BASE}/readings/${exampleDevice}?days=7"`} />
              ) : (
                <CodeBlock language="bash" code={
`# List your soil sensors
curl -H "Authorization: ApiKey YOUR_KEY" \\
  "${SOIL_BASE}/devices"

# Latest soil reading
curl -H "Authorization: ApiKey YOUR_KEY" \\
  "${SOIL_BASE}/readings/device/${exampleSoilDevice}"

# Historical (last 7 days)
curl -H "Authorization: ApiKey YOUR_KEY" \\
  "${SOIL_BASE}/readings/device/${exampleSoilDevice}?startDate=2025-01-01"`} />
              )}
            </div>

            {/* Python example */}
            <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 16, padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Code2 style={{ width: 15, height: 15, color: '#6366f1' }} />
                <p style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', margin: 0 }}>Python Example</p>
              </div>
              {docsTab === 'weather' ? (
                <CodeBlock language="python" code={
`import requests

API_KEY = "your_api_key_here"
DEVICE  = "${exampleDevice}"
BASE    = "${DATA_BASE}"
headers = {"Authorization": f"ApiKey {API_KEY}"}

r = requests.get(
    f"{BASE}/readings/{DEVICE}/latest",
    headers=headers
)
reading = r.json()["data"]["sensorData"]
print(f"Temp: {reading['airTemperature']}°C")
print(f"Humidity: {reading['airHumidity']}%")`} />
              ) : (
                <CodeBlock language="python" code={
`import requests

API_KEY = "your_api_key_here"
DEVICE  = "${exampleSoilDevice}"
BASE    = "${SOIL_BASE}"
headers = {"Authorization": f"ApiKey {API_KEY}"}

# Get all your soil sensors
devices = requests.get(
    f"{BASE}/devices", headers=headers
).json()["data"]

# Get latest readings for first sensor
r = requests.get(
    f"{BASE}/readings/device/{DEVICE}",
    headers=headers
)
readings = r.json()["data"]
latest = readings[0]["soilData"]
print(f"Moisture: {latest['hum']}%")
print(f"Soil Temp: {latest['temp']}°C")
print(f"pH: {latest['ph']}")`} />
              )}
            </div>

          </div>
        </div>
      </div>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}
